import React, { useState, useEffect, useRef } from 'react';
import { 
  ensureAudioResumed, 
  createBeachWaveSound, 
  playSeagullCall 
} from './audioUtils';

const BeachDay = ({ audioCtxRef, soundEnabled }) => {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const seagullIntervalRef = useRef(null);
  const wavePhaseRef = useRef(0);
  
  // Handle audio
  useEffect(() => {
    const audioCtx = audioCtxRef?.current;
    if (!soundEnabled || !audioCtx) return;
    
    // Start calm ocean wave sounds
    const startAudio = async () => {
      const isReady = await ensureAudioResumed(audioCtx);
      if (!isReady) return;
      
      try {
        audioRef.current = createBeachWaveSound(audioCtx);
        
        // Add occasional seagull calls
        seagullIntervalRef.current = setInterval(() => {
          if (Math.random() > 0.5) {
            playSeagullCall(audioCtx);
            // Sometimes a second call in response
            if (Math.random() > 0.6) {
              setTimeout(() => playSeagullCall(audioCtx), 300 + Math.random() * 500);
            }
          }
        }, 4000 + Math.random() * 3000);
        
      } catch (e) { console.warn('Beach audio error:', e); }
    };
    
    startAudio();
    
    return () => {
      if (audioRef.current && audioCtx) {
        try {
          audioRef.current.gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.5);
          setTimeout(() => {
            try { audioRef.current?.source?.stop(); } catch { /* cleanup */ }
          }, 600);
        } catch { /* cleanup */ }
      }
      if (seagullIntervalRef.current) {
        clearInterval(seagullIntervalRef.current);
      }
    };
  }, [soundEnabled, audioCtxRef]);
  
  // Subtle wave animation overlay
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Get the actual rendered size from parent container
    const container = canvas.parentElement;
    const rect = container.getBoundingClientRect();
    
    // Set canvas size to match container exactly
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    const ctx = canvas.getContext('2d', { alpha: true });
    
    const animate = () => {
      wavePhaseRef.current += 0.025;
      
      // Clear canvas with transparency
      ctx.clearRect(0, 0, rect.width, rect.height);
      
      // Ocean area - adjusted to match the actual water in the image
      // Water starts around 52% from top and beach starts around 72%
      const oceanTop = rect.height * 0.52;
      const oceanBottom = rect.height * 0.70;
      
      // Animated wave foam lines - white highlights on the water ONLY
      ctx.lineCap = 'round';
      
      // Multiple layers of wave highlights
      for (let layer = 0; layer < 6; layer++) {
        const layerY = oceanTop + (layer / 6) * (oceanBottom - oceanTop);
        const opacity = 0.3 - layer * 0.035;
        const lineWidth = 2 - layer * 0.2;
        
        ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.lineWidth = lineWidth;
        
        ctx.beginPath();
        for (let x = 0; x <= rect.width; x += 3) {
          const waveHeight = 3 + layer * 1.2;
          const frequency = 0.015 + layer * 0.002;
          const y = layerY + 
            Math.sin(x * frequency + wavePhaseRef.current * (1 + layer * 0.1) + layer * 0.8) * waveHeight +
            Math.sin(x * 0.008 + wavePhaseRef.current * 0.7) * 2;
          
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      
      // Sparkle/shimmer effects on the water
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      for (let i = 0; i < 10; i++) {
        const sparkleX = (Math.sin(wavePhaseRef.current * 0.3 + i * 2.5) * 0.5 + 0.5) * rect.width;
        const sparkleY = oceanTop + Math.random() * (oceanBottom - oceanTop) * 0.6;
        const sparkleSize = 1 + Math.sin(wavePhaseRef.current * 2 + i);
        
        if (sparkleSize > 0.5) {
          ctx.beginPath();
          ctx.arc(sparkleX, sparkleY, sparkleSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      
      // Shore foam - animated white foam at the beach edge
      const shoreY = rect.height * 0.66;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let x = 0; x <= rect.width; x += 2) {
        const y = shoreY + Math.sin(x * 0.02 + wavePhaseRef.current * 1.5) * 3 + 
                  Math.sin(x * 0.04 + wavePhaseRef.current * 2) * 2;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      
      requestAnimationFrame(animate);
    };
    
    const animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);
  
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div 
        className="relative rounded-xl overflow-hidden shadow-2xl border-4 border-amber-800/50" 
        style={{ 
          width: '85vw', 
          maxWidth: '1000px', 
          height: '65vh', 
          maxHeight: '600px',
          backgroundColor: '#87CEEB'  /* Sky blue fallback */
        }}
      >
        {/* Beach scene background image */}
        <img 
          src="/images/beach_scene.png" 
          alt="Beach scene" 
          className="w-full h-full object-cover"
        />
        {/* Canvas for wave animation overlay */}
        <canvas 
          ref={canvasRef} 
          className="absolute top-0 left-0 w-full h-full pointer-events-none" 
        />
      </div>
      <p className="text-white/50 text-sm">Listen to the waves... 🌊</p>
    </div>
  );
};

export default BeachDay;
